<template>
	<div class="min-h-screen bg-background py-4 sm:py-6 px-3 sm:px-4 lg:px-6">
		<div class="w-full max-w-full mx-auto">
			<div
				class="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6"
			>
				<h1
					class="text-2xl sm:text-3xl font-bold text-text-primary mb-2 sm:mb-0"
				>
					Valuation Request
				</h1>
			</div>

			<div
				class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-6 sm:mb-8"
			>
				<div
					class="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6"
				>
					<div class="w-full sm:flex-1 overflow-x-auto pb-2 sm:pb-0">
						<Stepper :current="statusIndex" :steps="steps" />
					</div>
					<div
						class="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:gap-4 text-sm"
					>
						<div
							v-if="errMessage"
							class="px-3 py-1.5 sm:px-4 sm:py-2 bg-red-50 sm:bg-red-100 text-red-700 sm:text-red-800 rounded-lg font-medium text-xs sm:text-sm"
						>
							{{ errMessage }}
						</div>
						<div
							v-else-if="successMessage"
							class="px-3 py-1.5 sm:px-4 sm:py-2 bg-green-50 sm:bg-green-100 text-green-700 sm:text-green-800 rounded-lg font-medium text-xs sm:text-sm"
						>
							{{ successMessage }}
						</div>
						<div class="flex-shrink-0">
							<Badge :status="value.valuationRequestStatus" />
						</div>
						<div
							class="text-xs sm:text-sm text-text-secondary break-all"
						>
							ID:
							<span class="font-semibold text-text-primary">{{
								value.id
							}}</span>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="selectValuationData.length > 0"
				class="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6"
			>
				<div class="lg:col-span-2 space-y-4 sm:space-y-6">
					<div
						class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border overflow-hidden"
					>
						<div
							class="px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-background flex items-center justify-between"
						>
							<h2
								class="text-lg sm:text-xl font-semibold text-text-primary"
							>
								Summary
							</h2>
							<span
								class="text-xs sm:text-sm text-text-secondary"
							>
								Requested:
								<span class="font-medium text-text-primary">{{
									formatDate(value.valuationRequestDate)
								}}</span>
							</span>
						</div>
						<div class="p-4 sm:p-5 md:p-6">
							<div
								class="grid grid-cols-1 xs:grid-cols-2 gap-4 sm:gap-5"
							>
								<div
									class="bg-background p-3 sm:p-4 rounded-lg border border-border"
								>
									<p
										class="text-xs sm:text-sm font-medium text-text-secondary mb-1"
									>
										Status
									</p>
									<div class="flex items-center gap-2">
										<Badge
											:status="
												value.valuationRequestStatus
											"
											class="text-sm sm:text-base"
										/>
										<span
											class="text-sm sm:text-base font-medium text-text-primary"
											>{{
												value.valuationRequestStatus
											}}</span
										>
									</div>
								</div>

								<div
									class="bg-background p-3 sm:p-4 rounded-lg border border-border"
								>
									<p
										class="text-xs sm:text-sm font-medium text-text-secondary mb-1"
									>
										Expected Completion
									</p>
									<p
										class="text-sm sm:text-base font-semibold text-text-primary"
									>
										{{
											formatDate(
												value.expectedCompletionDate,
											) || 'Not set'
										}}
									</p>
								</div>
							</div>

							<div
								v-if="value.valuationRequestorRemarks"
								class="mt-4 bg-background p-3 sm:p-4 rounded-lg border border-border"
							>
								<p
									class="text-xs sm:text-sm font-medium text-text-secondary mb-2"
								>
									Remarks
								</p>
								<p
									class="text-sm sm:text-base text-text-primary leading-relaxed"
								>
									{{ value.valuationRequestorRemarks }}
								</p>
							</div>
						</div>
					</div>

					<div
						class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border overflow-hidden"
					>
						<div
							class="px-4 sm:px-6 py-3 sm:py-4 border-b border-border bg-background"
						>
							<h2
								class="text-lg sm:text-xl font-semibold text-text-primary"
							>
								Client Details
							</h2>
						</div>
						<div class="p-4 sm:p-5 md:p-6">
							<div
								v-for="(client, index) in value.client_details"
								:key="client.id"
								class="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 p-4 sm:p-5 bg-background rounded-lg border border-border mb-4 last:mb-0"
							>
								<div class="flex flex-col">
									<p
										class="text-xs font-medium text-text-secondary mb-1"
									>
										Full Name
									</p>
									<p
										class="text-sm font-medium text-text-primary"
									>
										{{ client.names.fname }}
										{{ client.names.lname }}
									</p>
								</div>
								<div class="flex flex-col">
									<p
										class="text-xs font-medium text-text-secondary mb-1"
									>
										Email
									</p>
									<a
										:href="'mailto:' + client.email"
										class="text-sm text-primary hover:underline break-all"
									>
										{{ client.email }}
									</a>
								</div>
								<div class="flex flex-col">
									<p
										class="text-xs font-medium text-text-secondary mb-1"
									>
										KRA PIN
									</p>
									<p
										class="text-sm font-mono text-text-primary"
									>
										{{ client.kraPin || '—' }}
									</p>
								</div>

								<!-- Additional client details can be added here with proper spacing -->
								<div v-if="client.phone" class="flex flex-col">
									<p
										class="text-xs font-medium text-text-secondary mb-1"
									>
										Phone
									</p>
									<a
										:href="'tel:' + client.phone"
										class="text-sm text-text-primary hover:text-primary"
									>
										{{ client.phone }}
									</a>
								</div>

								<div
									v-if="client.idNumber"
									class="flex flex-col"
								>
									<p
										class="text-xs font-medium text-text-secondary mb-1"
									>
										ID Number
									</p>
									<p class="text-sm text-text-primary">
										{{ client.idNumber }}
									</p>
								</div>
							</div>
						</div>
					</div>

					<div
						class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border overflow-hidden"
					>
						<div
							class="px-4 sm:px-6 py-3 sm:py-5 border-b border-border bg-background flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1 sm:gap-2"
						>
							<h2
								class="text-lg sm:text-xl font-semibold text-text-primary"
							>
								Policy & Risks
							</h2>
							<span
								class="text-xs sm:text-sm text-text-secondary"
							>
								Currency:
								<span class="font-medium text-text-primary">{{
									value.policy_details[0].quotationCurrency ||
									'-'
								}}</span>
							</span>
						</div>
						<div class="p-3 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
							<div
								v-for="pol in value.policy_details"
								:key="pol.id"
							>
								<div
									class="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6"
								>
									<div>
										<p class="text-sm text-text-secondary">
											Policy No.
										</p>
										<p
											class="font-medium text-text-primary"
										>
											{{ pol.policyNumber }}
										</p>
									</div>
									<div>
										<p class="text-sm text-text-secondary">
											Type
										</p>
										<p
											class="font-medium text-text-primary"
										>
											{{ pol.policyType }}
										</p>
									</div>
									<div>
										<p class="text-sm text-text-secondary">
											Cover
										</p>
										<p
											class="font-medium text-text-primary"
										>
											{{ pol.coverType }}
										</p>
									</div>
									<div>
										<p class="text-sm text-text-secondary">
											Premium
										</p>
										<p
											class="font-semibold text-text-primary"
										>
											{{
												Number(
													pol.quotationPremium
														.totalPremium,
												).toLocaleString()
											}}
											{{ pol.quotationCurrency }}
										</p>
									</div>
								</div>

								<div>
									<h4
										class="font-semibold text-text-primary mb-3"
									>
										Insured Assets
									</h4>
									<div class="overflow-x-auto -mx-2 sm:mx-0">
										<table
											class="min-w-full divide-y divide-border"
										>
											<thead class="bg-background">
												<tr>
													<th
														class="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
													>
														Ref
													</th>
													<th
														class="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
													>
														Type
													</th>
													<th
														class="px-3 py-2 sm:px-4 sm:py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider"
													>
														Value
													</th>
												</tr>
											</thead>
											<tbody
												class="bg-surface divide-y divide-border"
											>
												<tr
													v-for="risk in pol.risks"
													:key="risk.riskRef"
													class="hover:bg-background"
												>
													<td
														class="px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-text-primary"
													>
														{{ risk.riskRef }}
													</td>
													<td
														class="px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm text-text-secondary"
													>
														{{ risk.riskType }}
													</td>
													<td
														class="px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-medium text-text-primary"
													>
														{{ risk.value }}
													</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div
						class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border overflow-hidden"
					>
						<div
							class="px-4 sm:px-6 py-3 sm:py-5 border-b border-border bg-background"
						>
							<h2
								class="text-lg sm:text-xl font-semibold text-text-primary"
							>
								Attachments
							</h2>
						</div>
						<div class="p-3 sm:p-4 md:p-6">
							<div
								class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6"
							>
								<div class="relative flex-grow">
									<input
										ref="fileInput"
										type="file"
										accept="image/*,application/pdf"
										@change="onFileChange"
										multiple
										class="block w-full text-xs sm:text-sm text-text-secondary file:mr-2 sm:file:mr-4 file:py-2 sm:file:py-2.5 file:px-3 sm:file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-white hover:file:bg-primary-light file:truncate file:overflow-hidden"
									/>
								</div>
								<button
									@click="uploadAttachments"
									:disabled="uploading || !attachments.length"
									class="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-2.5 bg-primary text-white rounded-lg hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
								>
									{{ uploading ? 'Uploading...' : 'Upload' }}
								</button>
							</div>
							<div
								v-if="attachments.length"
								class="grid grid-cols-2 xs:grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4"
							>
								<div
									v-for="(f, idx) in attachments"
									:key="idx"
									class="border rounded-lg overflow-hidden bg-background hover:shadow-sm transition-shadow"
								>
									<div class="relative pt-[75%] bg-gray-50">
										<img
											v-if="f.type === 'image'"
											:src="f.preview"
											:alt="f.name || 'attachment image'"
											class="absolute inset-0 w-full h-full object-cover"
										/>
										<div
											v-else
											class="absolute inset-0 flex items-center justify-center text-text-secondary"
										>
											<span class="text-3xl sm:text-4xl"
												>PDF</span
											>
										</div>
									</div>
									<p
										class="p-2 text-[10px] xs:text-xs text-center truncate bg-surface text-text-primary"
									>
										{{ f.name }}
									</p>
								</div>
							</div>
						</div>
					</div>
				</div>

				<aside class="space-y-4 sm:space-y-6 lg:sticky lg:top-4">
					<div
						class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border p-4 sm:p-5 md:p-6"
					>
						<h3
							class="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4"
						>
							Assigned Underwriter
						</h3>
						<div
							v-for="uw in value.underwriter_details"
							:key="uw.id"
							class="space-y-2 sm:space-y-3"
						>
							<div>
								<p
									class="text-xs sm:text-sm text-text-secondary"
								>
									Name
								</p>
								<p
									class="text-sm sm:text-base font-medium text-text-primary"
								>
									{{ uw.name || 'Not assigned' }}
								</p>
							</div>
							<div>
								<p
									class="text-xs sm:text-sm text-text-secondary"
								>
									Status
								</p>
								<p
									class="text-sm sm:text-base font-medium text-text-primary"
								>
									{{ uw.status || '—' }}
								</p>
							</div>
						</div>
					</div>

					<div
						class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border p-4 sm:p-5 md:p-6"
					>
						<h3
							class="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4"
						>
							Select Preferred Valuer
						</h3>

						<div class="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
							<p
								class="text-xs sm:text-sm font-medium text-text-secondary"
							>
								Recommended Valuer
							</p>
							<div
								v-for="valuer in value.default_valuer"
								:key="valuer.serviceProviderSystemRef"
							>
								<label
									class="flex items-center justify-between p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition hover:shadow-sm hover:bg-primary-light/5"
									:class="
										serviceProviderRef ===
										valuer.serviceProviderSystemRef
											? 'border-primary bg-primary-light/20'
											: 'border-border'
									"
								>
									<span
										class="text-sm sm:text-base font-medium text-text-primary pr-2"
									>
										{{
											valuer.serviceProviderNames ||
											'Preferred Valuer'
										}}
									</span>
									<input
										type="radio"
										name="valuer"
										v-model="serviceProviderRef"
										:value="valuer.serviceProviderSystemRef"
										class="w-4 h-4 sm:w-5 sm:h-5 text-primary focus:ring-primary flex-shrink-0"
									/>
								</label>
							</div>
						</div>

						<div class="space-y-2 sm:space-y-3">
							<p
								class="text-xs sm:text-sm font-medium text-text-secondary"
							>
								Other Available Valuers
							</p>
							<div
								v-for="valuer in value.other_valuers"
								:key="valuer.serviceProviderSystemRef"
							>
								<label
									class="flex items-center justify-between p-3 sm:p-4 rounded-lg border cursor-pointer transition hover:shadow-sm hover:bg-surface/50"
									:class="
										serviceProviderRef ===
										valuer.serviceProviderSystemRef
											? 'border-primary bg-primary-light/20'
											: 'border-border'
									"
								>
									<span
										class="text-sm sm:text-base font-medium text-text-primary pr-2"
									>
										{{
											valuer.serviceProviderContacts[0]
												.name || 'Valuer'
										}}
									</span>
									<input
										type="radio"
										name="valuer"
										v-model="serviceProviderRef"
										:value="valuer.serviceProviderSystemRef"
										class="w-4 h-4 sm:w-5 sm:h-5 text-primary focus:ring-primary flex-shrink-0"
									/>
								</label>
							</div>
						</div>
					</div>

					<div
						class="bg-primary rounded-lg sm:rounded-xl shadow-lg p-4 sm:p-5 md:p-6 text-white"
					>
						<h3
							class="text-base sm:text-lg font-semibold mb-2 sm:mb-3"
						>
							Ready to Proceed?
						</h3>
						<p
							class="text-white/90 text-xs sm:text-sm mb-4 sm:mb-6"
						>
							Select your preferred valuer, inspection time, and
							upload documents.
						</p>
						<button
							@click="openAttachments"
							class="w-full py-2.5 sm:py-3 bg-accent text-white rounded-lg hover:bg-accent-light transition font-medium text-sm sm:text-base"
						>
							Upload Documents
						</button>
					</div>

					<div
						class="bg-surface rounded-lg sm:rounded-xl shadow-sm border border-border p-4 sm:p-5 md:p-6"
					>
						<h3
							class="text-base sm:text-lg font-semibold text-text-primary mb-3 sm:mb-4"
						>
							Preferred Inspection Time (within 14 days)
						</h3>
						<div
							v-if="
								value.clientAvailability &&
								(value.clientAvailability.availableDate ||
									value.clientAvailability.availableTime)
							"
							class="mb-3 text-xs sm:text-sm text-text-secondary bg-blue-50 p-2 sm:p-3 rounded-lg"
						>
							<span class="font-medium text-text-primary"
								>Scheduled:</span
							>
							<span class="ml-1"
								>{{
									formatShortDate(
										value.clientAvailability.availableDate,
									)
								}}<span
									v-if="
										value.clientAvailability.availableTime
									"
								>
									·
									{{
										formatTime(
											value.clientAvailability
												.availableTime,
										)
									}}</span
								></span
							>
						</div>

						<form
							@submit.prevent="updateClientAvailability"
							class="space-y-3 sm:space-y-4"
						>
							<div
								class="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4"
							>
								<div>
									<label
										for="inspection-date"
										class="block text-xs sm:text-sm font-medium text-text-secondary mb-1 sm:mb-2"
										>Date</label
									>
									<input
										id="inspection-date"
										v-model="availability.date"
										type="date"
										:min="inspectionMinDate"
										:max="inspectionMaxDate"
										class="block w-full px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
									/>
								</div>
								<div>
									<label
										for="inspection-time"
										class="block text-xs sm:text-sm font-medium text-text-secondary mb-1 sm:mb-2"
										>Time</label
									>
									<input
										id="inspection-time"
										v-model="availability.time"
										type="time"
										class="block w-full px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
									/>
								</div>
							</div>

							<div
								class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3"
							>
								<button
									type="submit"
									:disabled="
										isSavingAvailability ||
										!availability.date ||
										!availability.time ||
										!serviceProviderRef
									"
									class="w-full sm:w-auto inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-primary text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-primary-light disabled:bg-gray-300 disabled:cursor-not-allowed transition flex-shrink-0"
								>
									<svg
										class="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M5 13l4 4L19 7"
										></path>
									</svg>
									{{
										isSavingAvailability
											? 'Saving...'
											: 'Save'
									}}
								</button>
								<div
									class="text-xs sm:text-sm text-text-secondary"
								>
									<p class="mb-0.5 sm:mb-1">
										Select valuer, then save.
									</p>
									<p
										class="text-[10px] xs:text-xs text-text-secondary/80"
									>
										<span class="font-medium">{{
											formatShortDate(inspectionMinDate)
										}}</span>
										—
										<span class="font-medium">{{
											formatShortDate(inspectionMaxDate)
										}}</span>
									</p>
								</div>
							</div>

							<p
								v-if="
									!availability.date ||
									!availability.time ||
									!serviceProviderRef
								"
								class="mt-1 text-xs sm:text-sm text-amber-700"
							>
								Please select date, time, and preferred valuer
								to save.
							</p>
						</form>
					</div>
				</aside>
			</div>

			<div v-else class="text-center py-16 sm:py-20 md:py-24">
				<div
					class="bg-red-50 border border-red-200 rounded-lg sm:rounded-xl p-6 sm:p-8 max-w-md mx-4 sm:mx-auto"
				>
					<h2 class="text-xl sm:text-2xl font-bold text-red-800">
						No Valuation Data Available
					</h2>
				</div>
			</div>

			<div
				class="mt-8 sm:mt-10 text-center text-xs sm:text-sm text-text-secondary"
			>
				Last updated: {{ formatDate(value.updatedAt) }}
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
	GetValuationDetails,
	UpdateValuationAvailbility,
} from '../../api/insurance';

export default {
	name: 'ValuationRequestClient',
	props: { request: String },

	components: {
		Stepper: {
			props: ['current', 'steps'],
			render(h) {
				const nodes = (this.steps || []).map((step, idx) => {
					const circleClass =
						idx <= this.current
							? 'bg-primary text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold'
							: 'bg-gray-100 text-text-secondary w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold';
					const labelClass =
						idx <= this.current
							? 'text-sm text-text-primary font-semibold'
							: 'text-sm text-text-secondary';
					return h(
						'div',
						{ key: idx, class: 'flex items-center gap-3' },
						[
							h('div', { class: circleClass }, String(idx + 1)),
							h('div', { class: 'hidden sm:block' }, [
								h(
									'div',
									{ class: labelClass },
									this.steps[idx],
								),
							]),
						],
					);
				});
				return h(
					'nav',
					{
						attrs: { 'aria-label': 'Progress' },
						class: 'flex items-center gap-4',
					},
					nodes,
				);
			},
		},
		Badge: {
			props: ['status'],
			computed: {
				classes() {
					const s = (this.status || '').toString().toUpperCase();
					if (s.includes('PENDING'))
						return 'bg-yellow-100 text-yellow-800';
					if (s.includes('COMPLETED'))
						return 'bg-green-100 text-green-800';
					if (s.includes('REJECTED'))
						return 'bg-red-100 text-red-800';
					if (
						s.includes('ASSIGNED') ||
						s.includes('PROCESSING') ||
						s.includes('IN_PROGRESS')
					)
						return 'bg-blue-100 text-blue-800';
					return 'bg-gray-100 text-gray-800';
				},
			},
			render(h) {
				return h(
					'div',
					{
						class: [
							'px-3 py-1 rounded-full text-sm font-semibold',
							this.classes,
						],
					},
					this.status || 'N/A',
				);
			},
		},
	},

	data() {
		return {
			attachments: [],
			availability: { date: '', time: '' },
			isSavingAvailability: false,
			uploading: false,
			steps: ['Created', 'Assigned', 'Inspection', 'Completed'],
			errMessage: '',
			successMessage: '',
			serviceProviderRef: '',
			loading: false,
		};
	},

	computed: {
		...mapGetters('valuation', ['selectValuationData']),
		value() {
			return this.selectValuationData[0] || {};
		},
		statusIndex() {
			const s = (this.value.valuationRequestStatus || '').toLowerCase();
			if (s.includes('pending')) return 0;
			if (s.includes('assigned')) return 1;
			if (s.includes('inspection') || s.includes('progress')) return 2;
			if (s.includes('completed')) return 3;
			return 0;
		},
		// added min/max inspection dates (today .. today + 14 days)
		inspectionMinDate() {
			const d = new Date();
			// format YYYY-MM-DD for input[type=date]
			const y = d.getFullYear();
			const m = String(d.getMonth() + 1).padStart(2, '0');
			const day = String(d.getDate()).padStart(2, '0');
			return `${y}-${m}-${day}`;
		},
		inspectionMaxDate() {
			const d = new Date();
			d.setDate(d.getDate() + 14);
			const y = d.getFullYear();
			const m = String(d.getMonth() + 1).padStart(2, '0');
			const day = String(d.getDate()).padStart(2, '0');
			return `${y}-${m}-${day}`;
		},
	},

	watch: {
		request: {
			immediate: true,
			handler() {
				this.fetchValuationData();
			},
		},
		value: {
			immediate: true,
			handler(val) {
				if (val.clientAvailability) {
					this.availability.date =
						val.clientAvailability.availableDate || '';
					this.availability.time =
						val.clientAvailability.availableTime || '';
				}
				// Auto-select default valuer if only one exists
				if (
					val.default_valuer.length === 1 &&
					!this.serviceProviderRef
				) {
					this.serviceProviderRef =
						val.default_valuer[0].serviceProviderSystemRef;
				}
			},
		},
	},

	methods: {
		...mapActions('valuation', ['setValuationData']),

		formatDate(date) {
			if (!date) return '-';
			return new Date(date).toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			});
		},
		formatShortDate(isoString) {
			if (!isoString) return '-';
			const d = new Date(isoString);
			return d.toLocaleDateString('en-GB', {
				day: 'numeric',
				month: 'short',
				year: 'numeric',
			});
		},
		// added small time formatter for friendly display
		formatTime(timeString) {
			if (!timeString) return '';
			// if input is already HH:MM, return it; otherwise try to parse
			if (/^\d{2}:\d{2}$/.test(timeString)) return timeString;
			const d = new Date(timeString);
			if (isNaN(d)) return timeString;
			return d.toLocaleTimeString([], {
				hour: '2-digit',
				minute: '2-digit',
			});
		},

		async fetchValuationData() {
			if (!this.request) return;
			try {
				const payload = { valuationRequestSystemRef: this.request };
				const res = await GetValuationDetails(payload);
				if (res.status && res.data) {
					this.setValuationData(res.data);
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Quotation Request',
						message: 'Quotation Request fetched',
						duration: 4000,
					});
				}
			} catch (err) {
				console.error('fetchValuationData error', err);
			}
		},

		async updateClientAvailability() {
			if (this.isSavingAvailability) return;
			if (!this.availability.date || !this.availability.time) {
				this.errMessage = 'Please select both date and time';
				return;
			}
			if (!this.serviceProviderRef) {
				this.errMessage = 'Please select a preferred valuer';
				return;
			}

			const payload = {
				valuationRequestSystemRef: this.value.valuationRequestSystemRef,
				serviceProviderSystemref: this.serviceProviderRef,
				clientAvailability: {
					availableDate: this.availability.date,
					availableTime: this.availability.time,
				},
			};

			this.isSavingAvailability = true;
			this.errMessage = '';
			this.successMessage = '';
			await new Promise((resolve) => setTimeout(resolve, 2000));
			this.loading = true;
			try {
				const res = await UpdateValuationAvailbility(payload);
				if (res.status) {
					this.setValuationData([
						{
							...this.value,
							clientAvailability: payload.clientAvailability,
						},
					]);
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Request Updated',
						message: `Request submitted for processing`,
						duration: 3000,
					});

					this.successMessage =
						'Availability & valuer saved successfully!';
					setTimeout(() => (this.successMessage = ''), 4000);
				}
			} catch (error) {
				this.errMessage =
					error.response &&
					error.response.data &&
					error.response.data.errors
						? error.response.data.errors
						: 'Failed to save';

				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Valuation error',
					message: `Failed to save. Please try again`,
					duration: 3000,
				});
			} finally {
				this.isSavingAvailability = false;
				this.loading = false;
			}
		},

		// Restored file handlers that were previously commented out
		onFileChange(e) {
			const files = Array.from(e.target.files || []);
			this.attachments = files.map((f) => {
				const isImage =
					f.type && f.type.startsWith && f.type.startsWith('image/');
				return {
					file: f,
					name: f.name,
					type: isImage ? 'image' : 'pdf',
					preview: isImage ? URL.createObjectURL(f) : null,
				};
			});
		},

		async uploadAttachments() {
			if (!this.attachments.length) return;
			this.uploading = true;
			try {
				// Example upload flow - replace Api.uploadAttachments with your real API call
				// const fd = new FormData();
				// this.attachments.forEach(a => fd.append('files', a.file));
				// await Api.uploadAttachments(fd);
				this.successMessage = 'Files prepared for upload';
			} catch (err) {
				this.errMessage = 'Upload failed';
			} finally {
				this.uploading = false;
			}
		},

		openAttachments() {
			this.$el
				.querySelector('input[type=file]')
				.scrollIntoView({ behavior: 'smooth' });
		},
	},

	mounted() {
		this.fetchValuationData();
	},
};
</script>
